import { compilePdf, prewarmEngine } from './engine/compile';
import { logTail, parseFirstError } from './latex/log';
import { SAMPLE_TEX } from './sample';
import { clearSaved, loadDocument, scheduleSave } from './storage';
import { createEditor } from './ui/editor';
import './style.css';

function el<T extends HTMLElement>(id: string, type: new () => T): T {
  const node = document.getElementById(id);
  if (!(node instanceof type)) throw new Error(`Missing element #${id}`);
  return node;
}

const compileBtn = el('compile', HTMLButtonElement);
const compileKbd = el('compile-kbd', HTMLElement);
const statusEl = el('status', HTMLElement);
const downloadPdfBtn = el('download-pdf', HTMLButtonElement);
const openPdfLink = el('open-pdf', HTMLAnchorElement);
const downloadTexBtn = el('download-tex', HTMLButtonElement);
const resetBtn = el('reset', HTMLButtonElement);
const previewEmpty = el('preview-empty', HTMLElement);
const previewReady = el('preview-ready', HTMLElement);
const openPdfInline = el('open-pdf-inline', HTMLAnchorElement);
const previewBusy = el('preview-busy', HTMLElement);
const previewError = el('preview-error', HTMLElement);
const errorHint = el('error-hint', HTMLElement);
const errorLog = el('error-log', HTMLPreElement);
const gotoLineBtn = el('goto-line', HTMLButtonElement);
const pdfFrame = el('pdf-frame', HTMLIFrameElement);
const tabEditor = el('tab-editor', HTMLButtonElement);
const tabPreview = el('tab-preview', HTMLButtonElement);

const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);
compileKbd.textContent = isMac ? '⌘↩' : 'Ctrl ↩';

const editor = createEditor(el('editor', HTMLElement), loadDocument() ?? SAMPLE_TEX, {
  onChange: scheduleSave,
  onCompileRequest: () => void compile(),
});

let pdfUrl: string | undefined;
let pdfBlob: Blob | undefined;
let compiling = false;

// iOS Safari does not render blob PDFs inside an iframe; hand those users
// explicit open and download actions instead of a blank pane.
const inlinePdfSupported = !/iPad|iPhone/.test(navigator.userAgent);

type PreviewState = 'empty' | 'busy' | 'error' | 'pdf' | 'ready';

function showPreview(state: PreviewState): void {
  previewEmpty.hidden = state !== 'empty';
  previewBusy.hidden = state !== 'busy';
  previewError.hidden = state !== 'error';
  previewReady.hidden = state !== 'ready';
  pdfFrame.hidden = state !== 'pdf';
}

function setStatus(text: string): void {
  statusEl.textContent = text;
}

async function compile(): Promise<void> {
  if (compiling) return;
  compiling = true;
  compileBtn.disabled = true;
  setStatus('Compiling…');
  showPreview('busy');

  const started = performance.now();
  try {
    const result = await compilePdf(editor.getText());
    if (result.status === 0 && result.pdf) {
      const blob = new Blob([result.pdf as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const previous = pdfUrl;
      pdfUrl = url;
      pdfBlob = blob;
      if (previous) {
        setTimeout(() => {
          URL.revokeObjectURL(previous);
        }, 10_000);
      }
      downloadPdfBtn.disabled = false;
      openPdfLink.href = url;
      openPdfLink.hidden = false;
      openPdfInline.href = url;
      if (inlinePdfSupported) {
        pdfFrame.src = url;
        showPreview('pdf');
      } else {
        showPreview('ready');
      }
      setStatus(`Compiled in ${((performance.now() - started) / 1000).toFixed(1)} s`);
      setView('preview');
    } else {
      presentFailure(result.log);
    }
  } catch {
    presentFailure(
      'The TeX engine could not be loaded. Check your connection and reload the page.',
    );
  } finally {
    compiling = false;
    compileBtn.disabled = false;
  }
}

function presentFailure(log: string): void {
  setStatus('Compile failed');
  const parsed = parseFirstError(log);
  errorHint.hidden = !parsed?.hint;
  errorHint.textContent = parsed?.hint ?? '';
  if (parsed?.line !== undefined) {
    const line = parsed.line;
    gotoLineBtn.hidden = false;
    gotoLineBtn.textContent = `Go to line ${String(line)}`;
    gotoLineBtn.onclick = () => {
      setView('editor');
      editor.gotoLine(line);
    };
  } else {
    gotoLineBtn.hidden = true;
  }
  errorLog.textContent = parsed
    ? `! ${parsed.message}\n\n${logTail(log)}`
    : logTail(log) || 'No log output.';
  showPreview('error');
  setView('preview');
}

function download(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10_000);
}

compileBtn.addEventListener('click', () => void compile());

// The editor binds Mod-Enter itself; this catches the shortcut when focus
// sits anywhere else (buttons, the log pane, the page body). A compile
// already in flight makes the double trigger from the editor path a no-op.
document.addEventListener('keydown', (ev) => {
  if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
    ev.preventDefault();
    void compile();
  }
});

downloadTexBtn.addEventListener('click', () => {
  download('document.tex', new Blob([editor.getText()], { type: 'text/x-tex' }));
});

downloadPdfBtn.addEventListener('click', () => {
  // Straight from the kept blob: fetching the blob URL instead would run
  // afoul of the production CSP (connect-src 'self').
  if (pdfBlob) download('document.pdf', pdfBlob);
});

resetBtn.addEventListener('click', () => {
  if (!window.confirm('Replace the current document with the starter example?')) return;
  editor.setText(SAMPLE_TEX);
  clearSaved();
  editor.focus();
});

/** Narrow screens show one pane at a time; these tabs switch between them. */
function setView(view: 'editor' | 'preview'): void {
  document.body.dataset.view = view;
  tabEditor.setAttribute('aria-pressed', String(view === 'editor'));
  tabPreview.setAttribute('aria-pressed', String(view === 'preview'));
}

tabEditor.addEventListener('click', () => {
  setView('editor');
  editor.focus();
});
tabPreview.addEventListener('click', () => {
  setView('preview');
});

prewarmEngine();

// Deliberate automation hook: compiles a document without touching the UI.
// The TeX Live harvest script and the e2e suite drive the engine through it.
declare global {
  interface Window {
    compileTexForTest?: (tex: string) => Promise<{ status: number; log: string; pdfBytes: number }>;
  }
}

window.compileTexForTest = async (tex) => {
  const result = await compilePdf(tex);
  return { status: result.status, log: result.log, pdfBytes: result.pdf?.length ?? 0 };
};
